import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Build any report, no SQL required',
    description:
      'Pick columns, filters, groupings, and charts from a visual builder, across 24 identity data sources. Define your own report types when the built-ins are not enough.',
    bullets: [
      '24 data sources across 5 categories',
      'Custom report types over 14 reportable entities',
      'Visual join canvas: no queries to write',
      'Async runs with live status, even on large tenants',
    ],
  },
  {
    title: 'Deliver on a schedule that never drifts',
    description:
      'Daily, weekly, or monthly: reports arrive where your process expects them, with daylight-saving handled correctly.',
    bullets: [
      'CSV, XLSX, and PDF export with embedded charts',
      'Email delivery as attachment or link',
      'HMAC-signed webhooks into SIEM / ITSM',
      'Self-service subscriptions per stakeholder',
    ],
  },
  {
    title: 'Compliance evidence, without the scramble',
    description:
      'Audit season stops being a project. Templates and masking tiers produce evidence that is complete for auditors and safe for inboxes.',
    bullets: [
      'Compliance report templates, ready to schedule',
      'Executive summary for leadership reviews',
      'PII-masking tiers per audience',
      'Tenant-scoped RBAC sharing',
    ],
  },
  {
    title: 'Every access change, on one timeline',
    description:
      'The Access Timeline shows role, entitlement, group, resource, and profile changes as one filterable stream: who changed what access, and when.',
    bullets: [
      'Roles, permissions, groups, apps, and profile values',
      'Filter by user, object, action, or period',
      'Rendered in the viewer’s local timezone',
      'The answer to “who gave them that?” in seconds',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Pick your data',
    description:
      'Choose a data source or define a custom report type on the visual canvas. Columns, filters, grouping, done.',
  },
  {
    title: 'Schedule delivery',
    description:
      'Set the cadence and the channel: inbox, link, or signed webhook straight into your SIEM or ITSM.',
  },
  {
    title: 'Share and subscribe',
    description:
      'Publish with RBAC scoping and masking tiers; stakeholders subscribe themselves to what they need.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Audit & compliance',
    description:
      'The use case: evidence collection as a routine, not a fire drill.',
    href: '/solutions/audit-compliance',
  },
  {
    label: 'Identity automation',
    description:
      'The triggers and workflows these reports keep honest.',
    href: '/sign/automation',
  },
  {
    label: 'User lifecycle',
    description:
      'Certifications and reviews that feed the audit trail.',
    href: '/sign/lifecycle',
  },
]

export default function ReportingPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Identity reporting & access timeline"
        description="No-SQL report builder over 24 identity data sources, scheduled delivery to email or signed SIEM webhooks, PII masking, compliance templates, and a timeline of every access change."
        path="/sign/reporting"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosign · Reporting"
          title={
            <>
              Every identity question,
              <br />
              answered on schedule.
            </>
          }
          body="Who has access to what, who granted it, and when did it change? Monosign turns identity data into reports your auditors, executives, and SOC actually receive: built visually, delivered on schedule, masked per audience."
          badges={[
            'No-SQL report builder',
            'Scheduled email + signed webhooks',
            'PII masking built in',
          ]}
        />
        <SolutionPillars
          title={<>From identity data to delivered evidence.</>}
          body="Reporting is not an export button. Monosign treats reports as governed objects: built, scheduled, scoped, masked, and audited like everything else on the platform."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Three steps to reports that run themselves.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
